import type { ChangeEvent, FC } from 'react';

import { cn } from '@/utils/cn.util';
import type { ListingCategory, ListingSort, RentalTab } from '@/types/rental.types';

import { CATEGORY_FILTERS, LISTING_TABS, RENTAL_TABS, SORTS } from '../constants';

interface RentalsToolbarProps {
  tab: RentalTab;
  search: string;
  category: ListingCategory | '';
  campusId: string;
  sort: ListingSort;
  campuses: { id: string; name: string }[];
  disputeCount: number;
  onChangeTab: (tab: RentalTab) => void;
  onChangeSearch: (value: string) => void;
  onChangeCategory: (category: ListingCategory | '') => void;
  onChangeCampus: (campusId: string) => void;
  onChangeSort: (sort: ListingSort) => void;
}

const chipBase =
  'cursor-pointer rounded-full border border-[var(--color-divider)] px-3 py-1.5 text-[11.5px] font-semibold text-[var(--color-ink-muted)] transition-colors hover:border-brand-500 hover:bg-brand-500 hover:text-white';
const chipActive = 'border-brand-500 bg-brand-500 text-white hover:bg-brand-500';

const selectStyles =
  'h-8 cursor-pointer appearance-none rounded-lg border border-[var(--color-divider)] bg-[var(--color-surface-muted)] px-3 pr-8 text-[12.5px] font-medium text-[var(--color-ink-muted)] focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30';

const RentalsToolbar: FC<RentalsToolbarProps> = ({
  tab,
  search,
  category,
  campusId,
  sort,
  campuses,
  disputeCount,
  onChangeTab,
  onChangeSearch,
  onChangeCategory,
  onChangeCampus,
  onChangeSort,
}) => {
  const isListingTab = LISTING_TABS.includes(tab);

  const handleCategory = (e: ChangeEvent<HTMLSelectElement>): void =>
    onChangeCategory(e.target.value as ListingCategory | '');
  const handleCampus = (e: ChangeEvent<HTMLSelectElement>): void => onChangeCampus(e.target.value);
  const handleSort = (e: ChangeEvent<HTMLSelectElement>): void =>
    onChangeSort(e.target.value as ListingSort);
  const handleSearch = (e: ChangeEvent<HTMLInputElement>): void => onChangeSearch(e.target.value);

  return (
    <div className="flex flex-col gap-3 border-b border-[var(--color-divider)] px-4 py-3 md:flex-row md:flex-wrap md:items-center md:px-5">
      <div className="flex flex-wrap gap-1.5">
        {RENTAL_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={cn(chipBase, tab === t.key && chipActive)}
            onClick={() => onChangeTab(t.key)}
          >
            {t.key === 'disputes' && disputeCount > 0 ? `${t.label} (${disputeCount})` : t.label}
          </button>
        ))}
      </div>

      {tab !== 'disputes' ? (
        <div className="flex flex-wrap items-center gap-2 md:ml-auto">
          <div className="relative w-full sm:w-52">
            <input
              type="search"
              value={search}
              onChange={handleSearch}
              placeholder={isListingTab ? '🔍 Search listings…' : '🔍 Search booking code…'}
              className="h-8 w-full rounded-lg border border-[var(--color-divider)] bg-[var(--color-surface-muted)] px-3 text-[12.5px] text-[var(--color-ink)] placeholder:text-[var(--color-ink-soft)] focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
            />
          </div>

          {isListingTab ? (
            <div className="relative">
              <select value={category} onChange={handleCategory} className={selectStyles}>
                {CATEGORY_FILTERS.map((c) => (
                  <option key={c.key} value={c.key}>
                    {c.label}
                  </option>
                ))}
              </select>
              <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-[var(--color-ink-soft)]">
                ▼
              </span>
            </div>
          ) : null}

          <div className="relative">
            <select value={campusId} onChange={handleCampus} className={selectStyles}>
              <option value="">🔽 All Campuses</option>
              {campuses.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-[var(--color-ink-soft)]">
              ▼
            </span>
          </div>

          {isListingTab ? (
            <div className="relative">
              <select value={sort} onChange={handleSort} className={selectStyles}>
                {SORTS.map((s) => (
                  <option key={s.key} value={s.key}>
                    ↕️ Sort: {s.label}
                  </option>
                ))}
              </select>
              <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-[var(--color-ink-soft)]">
                ▼
              </span>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default RentalsToolbar;
